import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";
import { Badge, Button, Spinner } from "flowbite-react";
import {
  HiSparkles,
  HiChartBar,
  HiShieldCheck,
  HiArrowPath,
} from "react-icons/hi2";
import { HiOutlineSparkles, HiOutlineShieldCheck } from "react-icons/hi";
import { useQuery } from "react-query";
import axiosInstance from "../config/axiosInstance";
import { DASHBOARD, CRM } from "../config/apiConfig";

const rangeOptions = [
  { label: "Últimos 7 dias", value: "7d" },
  { label: "Últimos 30 dias", value: "30d" },
  { label: "Últimos 60 dias", value: "60d" },
  { label: "Últimos 90 dias", value: "90d" },
];

const currency = (value) =>
  (Number(value) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const formatDecisionDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const fetchOperationsDashboard = (range, funnelId) =>
  axiosInstance
    .get(DASHBOARD.GET_OPERATIONS, {
      params: { range, funnelId },
    })
    .then((response) => response.data.data);

const fetchFunnels = () =>
  axiosInstance.get(CRM.FUNNELS).then((response) => response.data.data ?? []);

export default function DashboardCRMNew() {
  const brand = "#A60B3E";
  const [range, setRange] = useState("30d");
  const rangeLabel =
    rangeOptions.find((option) => option.value === range)?.label ||
    rangeOptions[1].label;
  const [selectedFunnelId, setSelectedFunnelId] = useState(null);

  const { data, isLoading, isError, error, refetch } = useQuery(
    ["operationsDashboard", range, selectedFunnelId],
    () => fetchOperationsDashboard(range, selectedFunnelId),
    { enabled: Boolean(selectedFunnelId) }
  );

  const {
    data: funnels = [],
    isLoading: funnelsLoading,
    isError: funnelsError,
    error: funnelsErrorObj,
    refetch: refetchFunnels,
  } = useQuery("crmFunnels", fetchFunnels);

  useEffect(() => {
    if (!selectedFunnelId && funnels.length) {
      setSelectedFunnelId(funnels[0].id);
    }
  }, [funnels, selectedFunnelId]);

  const selectedFunnel = funnels.find(
    (funnel) => funnel.id === selectedFunnelId
  );
  const hasFunnelSelection = Boolean(selectedFunnelId);
  const noFunnelConfigured = !funnelsLoading && !funnels.length;
  const waitingForFunnelSelection = !hasFunnelSelection && !noFunnelConfigured;

  const summary = data?.summary ?? {};
  const timeline = data?.timeline ?? [];
  const stageDurations = data?.stageDurations ?? [];
  const approvalsByBank = data?.approvalsByBank ?? [];
  const amplaDeclines = data?.ampliaDeclines ?? [];
  const declineReasons = data?.declineReasons ?? [];

  const timelineOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: { curve: "smooth", width: 3 },
      markers: { size: 3 },
      grid: { borderColor: "#E5E7EB" },
      dataLabels: { enabled: false },
      xaxis: {
        categories: timeline.map((item) => item.label),
        labels: { style: { fontSize: "12px" } },
        tickPlacement: "on",
      },
      yaxis: {
        labels: { formatter: (val) => Math.round(val).toString() },
      },
      colors: [brand],
      tooltip: {
        y: { formatter: (val) => `${Math.round(val)} negócios` },
      },
    }),
    [brand, timeline]
  );

  const timelineSeries = useMemo(
    () => [
      {
        name: "Negócios iniciados",
        data: timeline.map((item) => item.count),
      },
    ],
    [timeline]
  );

  const stageOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: { columnWidth: "55%", borderRadius: 6 },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: stageDurations.map((stage) => stage.stageName),
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: {
        labels: { formatter: (value) => `${value}d` },
      },
      grid: { borderColor: "#E5E7EB" },
      colors: [brand],
      tooltip: {
        y: {
          formatter: (val) => `${Number(val).toFixed(1)} dias`,
        },
      },
    }),
    [brand, stageDurations]
  );

  const stageSeries = useMemo(
    () => [
      {
        name: "Dias médios",
        data: stageDurations.map((stage) => Number(stage.averageDays ?? 0)),
      },
    ],
    [stageDurations]
  );

  const summaryCards = [
    {
      label: "Negócios iniciados",
      value: summary.dealsStarted ?? 0,
      note: rangeLabel,
      accent: "from-rose-500/60 to-orange-400/70",
      icon: <HiArrowPath className="text-xl text-white/80" />,
    },
    {
      label: "Valor aprovado",
      value: currency(summary.approvedValue),
      note: `${summary.banksEvaluated || 0} instituições`,
      accent: "from-emerald-500/70 to-emerald-700/70",
      icon: <HiOutlineShieldCheck className="text-xl text-white/80" />,
    },
    {
      label: "Valor reprovado",
      value: currency(summary.rejectedValue),
      note: "Baseado nas decisões",
      accent: "from-amber-400/80 to-amber-500/70",
      icon: <HiChartBar className="text-xl text-white/80" />,
    },
    {
      label: "Declinações Amplia",
      value: summary.amplaDeclines ?? 0,
      note: "motivos registrados",
      accent: "from-slate-600/80 to-slate-700/90",
      icon: <HiSparkles className="text-xl text-white/80" />,
    },
  ];

  const heroMetrics = [
    {
      label: "Negócios iniciados",
      value: summary.dealsStarted ?? 0,
      contrast: `${rangeLabel}`,
      icon: <HiSparkles className="text-2xl text-white" />,
    },
    {
      label: "Valor aprovado",
      value: currency(summary.approvedValue),
      contrast: `${summary.banksEvaluated || 0} instituições`,
      icon: <HiShieldCheck className="text-2xl text-white" />,
    },
    {
      label: "Declinações Amplia",
      value: summary.amplaDeclines ?? 0,
      contrast: "motivação registrada",
      icon: <HiChartBar className="text-2xl text-white" />,
    },
  ];

  const SummaryStatCard = ({ card }) => (
    <div
      className={[
        "relative rounded-3xl p-5 shadow-md overflow-hidden border border-white/20",
        "bg-gradient-to-br text-white",
        card.accent,
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/70">
          {card.label}
        </p>
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/20">
          {card.icon}
        </div>
      </div>
      <p className="text-3xl font-semibold leading-snug">{card.value}</p>
      <p className="text-[11px] text-white/70 mt-2">{card.note}</p>
      <div className="absolute -top-4 -right-4 h-20 w-20 rounded-full bg-white/10 blur-2xl" />
    </div>
  );

  const fancyCardClasses =
    "border border-gray-100 bg-white shadow-md rounded-3xl p-5 flex flex-col gap-2";

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white rounded-3xl p-6 border border-white/10 shadow-md relative overflow-hidden min-h-[220px]"
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex flex-col gap-1 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.4em] text-white/70">
              Operações Corporativas
            </p>
            <div className="flex flex-wrap items-baseline gap-2">
              <h2 className="font-semibold text-3xl md:text-4xl">
                {selectedFunnel?.name || "Quadro corporativo"}
              </h2>
            </div>
            <p className="text-sm text-white/70 max-w-2xl leading-relaxed">
              Métricas filtradas por quadro e período selecionado. Os cartões
              abaixo trazem aprovações, recusas e tempos em cada etapa para o
              time responsável.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 text-slate-200">
            <div className="flex flex-col text-xs uppercase tracking-[0.4em] gap-2">
              <span className="text-white/70">Quadro</span>
              <select
                value={selectedFunnelId || ""}
                onChange={(event) => setSelectedFunnelId(event.target.value)}
                className="border border-white/40 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
                disabled={funnelsLoading || noFunnelConfigured}
              >
                {!selectedFunnelId && (
                  <option value="" disabled>
                    {funnelsLoading ? "Carregando..." : "Selecione"}
                  </option>
                )}
                {funnels.map((funnel) => (
                  <option key={funnel.id} value={funnel.id}>
                    {funnel.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col text-xs uppercase tracking-[0.4em] gap-2">
              <span className="text-white/70">Período</span>
              <select
                value={range}
                onChange={(event) => setRange(event.target.value)}
                className="border border-white/40 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
              >
                {rangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {funnelsError && (
          <p className="text-[11px] text-rose-200 mt-3">
            Falha ao carregar os quadros: {funnelsErrorObj?.message || "erro"}
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {heroMetrics.map((metric) => (
            <div
              key={metric.label}
              className="bg-white/10 border border-white/20 rounded-2xl p-4 backdrop-blur shadow-md"
            >
              <div className="flex items-center justify-between text-white mb-2">
                <p className="text-[11px] uppercase tracking-[0.3em] text-white/70">
                  {metric.label}
                </p>
                <span>{metric.icon}</span>
              </div>
              <p className="text-3xl font-semibold leading-none">
                {metric.value}
              </p>
              <p className="text-[11px] text-white/70 mt-2">
                {metric.contrast}
              </p>
            </div>
          ))}
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.25),_transparent_40%)] pointer-events-none" />
      </motion.div>

      {isError && (
        <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-sm text-red-700">
          <span>
            Falha ao carregar o painel: {error?.message || "Erro desconhecido"}
          </span>
        </div>
      )}

      {hasFunnelSelection ? (
        isLoading && !data ? (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm py-16 flex flex-col items-center justify-center gap-3">
            <Spinner size="xl" aria-label="Carregando painel" />
            <p className="text-sm text-gray-500">
              Carregando dados estratégicos...
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Negócios iniciados
                    </h2>
                    <p className="text-xs text-gray-500">
                      Evolução diária · {selectedFunnel?.name || "Quadro"}
                    </p>
                  </div>
                  <Badge color="light" className="!text-gray-700">
                    {timeline.length} dias
                  </Badge>
                </div>
                {timeline.length ? (
                  <ReactApexChart
                    options={timelineOptions}
                    series={timelineSeries}
                    type="line"
                    height={300}
                  />
                ) : (
                  <div className="py-10 text-center text-sm text-gray-500">
                    Sem negócios registrados nesse período.
                  </div>
                )}
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-5 space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Tempo médio por etapa
                  </h2>
                  <p className="text-xs text-gray-500">
                    Dados calculados a partir das movimentações registradas
                  </p>
                </div>
                {stageDurations.length ? (
                  <>
                    <ReactApexChart
                      options={stageOptions}
                      series={stageSeries}
                      type="bar"
                      height={260}
                    />
                    <div className="space-y-2">
                      {stageDurations.slice(0, 4).map((stage) => {
                        const avgDays = Number(stage.averageDays ?? 0);
                        const avgHours = Number(stage.averageHours ?? 0);
                        return (
                          <div
                            key={stage.stageId}
                            className="flex items-center justify-between text-sm"
                          >
                            <div>
                              <p className="font-semibold text-gray-900">
                                {stage.stageName}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {stage.entries} movimentos
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-gray-900">
                                {avgDays.toFixed(1)}d
                              </p>
                              <p className="text-[11px] text-gray-500">
                                ≈ {avgHours.toFixed(1)}h
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="py-10 text-center text-sm text-gray-500">
                    Ainda não há trajetórias completas registradas.
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Valores aprovados por instituição financeira
                    </h2>
                    <p className="text-xs text-gray-500">
                      Lista com as decisões mais recentes para cada instituição
                    </p>
                  </div>
                  <Badge color="pink" className="!bg-[#A60B3E] !text-white">
                    {approvalsByBank.length} I.F.
                  </Badge>
                </div>
                {approvalsByBank.length ? (
                  <div className="space-y-4 max-h-[440px] overflow-y-auto pr-2">
                    {approvalsByBank.map((bank) => (
                      <div
                        key={bank.bankId}
                        className="p-4 rounded-2xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 shadow-sm"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="space-y-0.5">
                            <p className="text-sm font-bold text-gray-900">
                              {bank.bankName}
                            </p>
                            <p className="text-[11px] text-gray-500">
                              {bank.decisions.length} decisões recentes
                            </p>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            <p className="text-[11px] text-gray-400">
                              Aprovado
                            </p>
                            <p className="font-semibold text-gray-900">
                              {currency(bank.approvedValue)}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1">
                              Reprovado
                            </p>
                            <p className="font-semibold text-rose-600">
                              {currency(bank.rejectedValue)}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 space-y-2">
                          {bank.decisions.map((decision, index) => (
                            <div
                              key={`${bank.bankId}-${decision.cardId}-${index}`}
                              className="flex items-center justify-between text-xs"
                            >
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {decision.cardTitle}
                                </p>
                                <p className="text-[11px] text-gray-500">
                                  {decision.stage || "—"}
                                </p>
                              </div>
                              <div className="text-right space-y-0.5">
                                <p className="text-[11px] text-gray-400">
                                  {formatDecisionDate(decision.decisionAt)}
                                </p>
                                <p className="font-semibold text-gray-900">
                                  {currency(decision.amount)}
                                </p>
                                <Badge
                                  color={
                                    decision.approved ? "success" : "failure"
                                  }
                                  className="text-[11px] px-2 py-1"
                                >
                                  {decision.approved ? "Aprovado" : "Reprovado"}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-10 text-center text-sm text-gray-500">
                    Nenhuma decisão financeira registrada no período.
                  </div>
                )}
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Recusas da Amplia Capital
                  </h2>
                  <p className="text-xs text-gray-500">
                    Rastreie motivos como vontade do cliente ou restrições.
                  </p>
                </div>
                {amplaDeclines.length ? (
                  <div className="space-y-3">
                    {amplaDeclines.map((decline) => (
                      <div
                        key={`${decline.cardId}-${decline.decisionAt}`}
                        className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {decline.cardTitle}
                            </p>
                            <p className="text-[11px] text-gray-500">
                              {decline.stage || "—"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold">
                              {currency(decline.amount)}
                            </p>
                            <p className="text-[11px] text-gray-500">
                              {formatDecisionDate(decline.decisionAt)}
                            </p>
                          </div>
                        </div>
                        <p className="text-[12px] text-gray-600 mt-2">
                          {decline.reason || "Motivo não informado"}
                        </p>
                        {decline.notes ? (
                          <p className="text-[11px] text-gray-500 mt-1">
                            {decline.notes}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-10 text-center text-sm text-gray-500">
                    Nenhuma recusa registrada pela Amplia Capital.
                  </div>
                )}
                {declineReasons.length ? (
                  <div className="mt-3">
                    <p className="text-[11px] font-semibold text-gray-500 mb-2">
                      Motivos mais recorrentes
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {declineReasons.map((reason) => (
                        <Badge
                          key={reason.reason}
                          color="light"
                          className="text-xs px-3 py-1 bg-gray-100 text-gray-500"
                        >
                          {reason.reason} · {reason.count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        )
      ) : null}
    </div>
  );
}
